import {ActionTree} from 'vuex';
import {Company, Reseller, RootState, SectorCheck, User, UserState} from '@/types';
import * as types from './types';
import axios from 'axios';
import {KEYCLOAK_LOGOUT_URL, redirectUri} from '@/constants/urls';
import {supportedSectors} from '@/static/supportedSectors';
import {API_CUSTOMER_URL, API_INVITATION_URL, API_USER_SUBSCRIPTIONS_URL, API_USER_URL} from '@/constants/api';
import {routes} from '@/static/routes';
import {NEWSLETTER} from '@/constants/subscriptions';
import messages from '@/static/messages.json';
import {KeycloakProfile} from "keycloak-js";
import {getKeycloak} from "@/components/auth/keycloak";

const actions: ActionTree<UserState, RootState> = {

  async postInvitationKey({commit}, key) {
    commit(types.PROCESSING_INVITATION_KEY, true);
    await axios.post(API_INVITATION_URL(key))
        .then(() => {
          getKeycloak().login(redirectUri())
        })
        .catch(err => {
          commit(types.INVITATION_KEY_ERROR_MESSAGE,
              err.response && err.response.data
                  ? parseInvitationKeyError(err.response.data.type,
                  err.response.data.product && err.response.data.product.reseller ? err.response.data.product.reseller : null)
                  : messages.INVITATION_KEY_GENERIC_ERROR);
        });
    commit(types.PROCESSING_INVITATION_KEY, false);
  },

  clearInvitationKeyError({commit}) {
    commit(types.INVITATION_KEY_ERROR_MESSAGE, null);
  },

  fetchUserProfile({commit}) {
    getKeycloak().loadUserProfile().success((data: KeycloakProfile) => {
      // @ts-ignore
      if (data.attributes && data.attributes['customer.uuid']) {
        // @ts-ignore
        commit(types.SET_UUID, data.attributes['customer.uuid'][0]);
      }
    });
  },

  async fetchUserDetails({commit}) {
    const {data: user} = await axios.get<User>(API_USER_URL);
    if (!user.company.sectors) {
      user.company.sectors = [];
    }
    const sectorChecks = toSectorCheck(user.company.sectors);
    commit(types.FETCH_USER_DETAILS_SUCCESS, prepareUserForUi(user, sectorChecks));
  },

  async fetchNewsletters({commit}) {
    const {data} = await axios.get(API_USER_SUBSCRIPTIONS_URL);
    commit(types.SET_SUBSCRIPTIONS, data);
  },

  saveSubscriptions({state}) {
    let subscriptions: string[] = [];
    if (state.subscriptions[0].checked) {
      subscriptions = [NEWSLETTER];
    }

    axios.put(API_USER_SUBSCRIPTIONS_URL, subscriptions);
  },

  resetPassword() {
    axios.post(`${API_USER_URL}/reset-password-mail`);
    window.location.href = KEYCLOAK_LOGOUT_URL;
  },

  async saveUserDetails({getters, commit}) {
    const {user} = getters;
    const updatedUser: User = {
      ...user,
      companySectors: getSelectedSectors(user.company.sectorChecks),
    };
    try {
      commit(types.SAVE_USER_DETAILS_LOADING, true);
      await axios.put(API_USER_URL, updatedUser); // will also update customer sectors
      await updateCompanyName(updatedUser.company);
    } finally {
      commit(types.SAVE_USER_DETAILS_LOADING, false);
    }
  },

  postUserInvitation() {
    alert('send invitation...');
  },

  async deleteUserAccount() {
    await axios.delete(API_USER_URL);
    window.location.href = routes.LOGIN;
  },
};

function prepareUserForUi(user: User, sectorChecks: SectorCheck[]): User {
  return {
    ...user,
    company: {
      ...user.company,
      sectorChecks: sectorChecks,
    },
  };
}

function toSectorCheck(sectors: string[]): SectorCheck[] {
  return supportedSectors.map(sector => ({
    ...sector,
    checked: Boolean(sectors.find((sec: string) => sec === sector.id)),
  }));
}

function getSelectedSectors(sectors: SectorCheck[]): string[] {
  return sectors
      .filter((item) => item.checked)
      .map((item) => item.id);
}

async function updateCompanyName(company: Company) {
  const url = API_CUSTOMER_URL(company.uuid);
  const {data} = await axios.get<Company>(url);
  const updatedCompany: Company = {...data, name: company.name};
  return axios.put(url, updatedCompany);
}

function parseInvitationKeyError(type?: string | null, reseller?: Reseller | null): string {
  switch (type) {
    case "https://sensefly.io/problem/entity-not-found":
      return messages.PRODUCT_NOT_FOUND;
    case "https://sensefly.io/problem/product-not-sold":
      let msg = messages.PRODUCT_NOT_SOLD;
      if (reseller) {
        msg += `<br/>Please contact your local senseFly distributor ${reseller.name}`;
        let email = null;
        if (reseller.email && reseller.email.trim().length > 0) {
          email = `<a href="mailto:${reseller.email.trim()}">${reseller.email.trim()}</a>`
        }
        const contact = [reseller.phone, email].filter(s => s && s.trim().length > 0).join(" / ");
        if (contact && contact.length > 0) {
          msg += ` at<br/>${contact}`
        }
      } else {
        msg += `<br/>Please contact senseFly Technical Support for help: <a href="mailto:support@sensefly.com">support@sensefly.com</a>`;
      }
      return msg;
    case "https://sensefly.io/problem/invitation-key-already-redeemed":
      return messages.INVITATION_KEY_ALREADY_REDEEMED;
    default:
      return messages.INVITATION_KEY_GENERIC_ERROR;
  }
}

export default actions;



// WEBPACK FOOTER //
// ./src/store/modules/user/actions.ts